import React, { useEffect, useState } from "react";
import { useParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "antd";

import { getEmailHistoryByLeadId } from "../../actionCreator/leadDetailsActionCreator";
import EmailAttachment from "./EmailAttachment";
import Loader from "../loader";
import HtmlRenderer from "./HtmlRenderer";
import EmailForm from "../../common/EmailForm";
import noEmailHistory from "../../assets/svg/no-email-history.svg";
import { ReactComponent as Email } from "../../assets/svg/icons/email.svg";
import moment from "moment";
import { formatTime } from "../../utils/formatDate";

const decodeBase64 = (str) => {
  if (!str) return "";
  try {
    const cleanStr = str.trim().replace(/\s/g, "");
    if (!/^[a-zA-Z0-9+/=-_]+$/.test(cleanStr)) {
      return str;
    }
    const standardBase64 = cleanStr.replace(/-/g, "+").replace(/_/g, "/");
    return decodeURIComponent(
      atob(standardBase64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join(""),
    );
  } catch (e) {
    try {
      const cleanStr = str
        .trim()
        .replace(/\s/g, "")
        .replace(/-/g, "+")
        .replace(/_/g, "/");
      return atob(cleanStr);
    } catch (err) {
      return str;
    }
  }
};

const getAvatarGradient = (email = "") => {
  const cleanEmail = email.trim().toLowerCase();
  if (!cleanEmail) {
    return "tw-from-indigo-500 tw-to-violet-600 tw-text-white";
  }

  let hash = 0;
  for (let i = 0; i < cleanEmail.length; i++) {
    hash = cleanEmail.charCodeAt(i) + ((hash << 5) - hash);
  }

  const gradients = [
    "tw-from-indigo-500 tw-to-purple-600 tw-text-white",
    "tw-from-emerald-500 tw-to-teal-600 tw-text-white",
    "tw-from-pink-500 tw-to-rose-500 tw-text-white",
    "tw-from-sky-500 tw-to-blue-600 tw-text-white",
    "tw-from-violet-500 tw-to-fuchsia-600 tw-text-white",
    "tw-from-amber-500 tw-to-orange-600 tw-text-white",
  ];

  const index = Math.abs(hash) % gradients.length;
  return gradients[index];
};

const EmailThreadMessage = ({
  data,
  leadDetail,
  isExpanded,
  onToggle,
  emailConversationHistory,
  isReply,
  isLast,
}) => {
  const dispatch = useDispatch();
  const [previewText, setPreviewText] = useState("");

  useEffect(() => {
    if (!data?.bodyS3Url) return;
    const fetchHtml = async () => {
      try {
        const response = await fetch(data.bodyS3Url);
        const text = await response.text();
        const decodedText = decodeBase64(text);

        // Unescape doubly-escaped HTML if needed
        const unescapedText =
          /&lt;\s*(div|p|br|span|html|body|table|tr|td|blockquote|a|h[1-6])/i.test(
            decodedText,
          )
            ? (() => {
                const tempDiv = document.createElement("div");
                tempDiv.innerHTML = decodedText;
                return tempDiv.textContent || tempDiv.innerText || decodedText;
              })()
            : decodedText;

        const parser = new DOMParser();
        const doc = parser.parseFromString(unescapedText, "text/html");

        // Strip any remaining HTML tags from the preview text
        const rawText = doc.body.textContent || "";
        const cleanText = rawText
          .replace(/<[^>]*>/g, " ")
          .replace(/\s+/g, " ")
          .trim();
        setPreviewText(
          cleanText.substring(0, 120) + (cleanText.length > 120 ? "..." : ""),
        );
      } catch (error) {
        console.error("Error fetching preview:", error);
      }
    };
    fetchHtml();
  }, [data?.bodyS3Url]);

  const isClientRevert = data?.sender === leadDetail?.emailId;

  return (
    <div className="tw-border-b tw-border-t-0 tw-border-l-0 tw-border-r-0 tw-border-solid tw-border-slate-100 last:tw-border-0 tw-relative">
      {/* Header Click Area to Toggle */}
      <div
        onClick={onToggle}
        className="tw-flex tw-justify-between tw-items-center tw-cursor-pointer tw-py-4 tw-px-5 hover:tw-bg-slate-50/50 tw-transition-all tw-duration-200"
      >
        <div className="tw-flex tw-items-center tw-gap-3 tw-min-w-0 tw-flex-1">
          <div
            className={`tw-size-8 tw-rounded-full tw-bg-gradient-to-br ${getAvatarGradient(data?.sender)} tw-font-bold tw-text-xs tw-flex tw-items-center tw-justify-center tw-shrink-0 tw-shadow-sm tw-relative tw-z-10 tw-border-2 tw-border-solid tw-border-white`}
          >
            {data?.sender ? data.sender.charAt(0).toUpperCase() : "U"}
          </div>
          <div className="tw-flex tw-flex-col tw-min-w-0 tw-flex-1">
            <div className="tw-flex tw-items-center tw-gap-2">
              <span className="tw-text-xs tw-font-semibold tw-text-slate-700 tw-truncate">
                {data?.sender}
              </span>
              <span className="tw-text-[10px] tw-text-slate-350 tw-select-none">
                •
              </span>
              <span
                className={`tw-text-[10px] tw-font-bold tw-px-2 tw-py-0.5 tw-rounded-md tw-tracking-wide tw-uppercase ${
                  isClientRevert
                    ? "tw-bg-emerald-50 tw-text-emerald-700 tw-border tw-border-solid tw-border-emerald-100/80"
                    : "tw-bg-indigo-50/80 tw-text-theme tw-border tw-border-solid tw-border-indigo-100/80"
                }`}
              >
                {isClientRevert ? "Client" : "You"}
              </span>
            </div>
            {!isExpanded && (
              <span className="tw-text-xs tw-text-slate-400 tw-truncate tw-mt-0.5">
                {previewText || "Loading preview..."}
              </span>
            )}
          </div>
        </div>
        <div className="tw-flex tw-items-center tw-gap-4 tw-shrink-0">
          <span className="tw-text-xs tw-text-slate-400 tw-font-medium">
            {formatTime(data?.createdDate)}
          </span>
          <i
            className={`las la-chevron-down tw-text-xs tw-text-slate-400 tw-transition-transform tw-duration-200 ${
              isExpanded ? "tw-rotate-180" : ""
            }`}
          />
        </div>
      </div>

      {/* Expanded Content Body */}
      {isExpanded && (
        <div className="tw-pl-[64px] tw-pr-6 tw-pb-6 tw-pt-1 tw-bg-slate-50/[0.04]">
          <div className="tw-text-[11px] tw-text-slate-400 tw-mb-3.5 tw-font-medium tw-flex tw-items-center tw-gap-1.5">
            <i className="las la-clock tw-text-xs" />
            Sent on{" "}
            {moment
              .utc(data?.createdDate)
              .local()
              .format("MMMM DD, YYYY [at] hh:mm A")}
          </div>
          {/* Attachments */}
          {!!data?.attachments?.length && (
            <div className="tw-flex tw-items-center tw-flex-wrap tw-gap-2.5 tw-mb-4">
              {data?.attachments?.map((d, i) => (
                <EmailAttachment key={i} attachment={d} />
              ))}
            </div>
          )}

          {/* Email Content HTML */}
          <div className="tw-text-sm tw-text-slate-600 tw-leading-relaxed tw-bg-white tw-border tw-border-solid tw-border-slate-100 tw-rounded-xl tw-p-5 tw-shadow-[0_4px_16px_rgba(0,0,0,0.02)] tw-break-words tw-max-w-full tw-max-h-[450px] tw-overflow-y-auto custom-scrollbar">
            <HtmlRenderer
              fileUrl={data?.bodyS3Url}
              attachments={data?.attachments}
            />
          </div>

          {/* Inline Reply Trigger */}
          {isLast && (
            <div className="tw-mt-4 tw-flex tw-justify-end">
              <EmailForm
                recipientEmail={leadDetail?.emailId}
                empId={leadDetail?.employeeId}
                payload={{
                  ...leadDetail,
                  messageId: data?.messageId,
                  threadId: data?.threadId,
                  subject: data.subject.toLowerCase().startsWith("re:")
                    ? data.subject
                    : `Re: ${data.subject}`,
                  emailHistoryList: emailConversationHistory
                    ?.slice(-3)
                    ?.map((d) => ({
                      subject: d?.subject,
                      bodyS3Url: d?.bodyS3Url,
                    })),
                }}
                callback={() =>
                  dispatch(getEmailHistoryByLeadId(leadDetail?.id))
                }
              >
                <Button className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-semibold tw-text-slate-600 hover:tw-text-theme tw-bg-white hover:tw-bg-indigo-50/30 tw-border tw-border-solid tw-border-slate-200 hover:tw-border-theme/30 tw-rounded-lg tw-px-3 tw-py-1.5 tw-transition-all tw-duration-200 tw-shadow-sm hover:tw-shadow-md">
                  <i className="las la-reply tw-text-sm" /> Reply
                </Button>
              </EmailForm>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const EmailHistory = () => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const loading = useSelector((state) => state.LeadDetailsReducer.loading);
  const emailConversationHistory = useSelector(
    (state) => state.LeadDetailsReducer.emailConversationHistory,
  );
  const leadDetail = useSelector(
    (state) => state.LeadDetailsReducer.leadDetails,
  );

  const [expandedEmails, setExpandedEmails] = useState({});
  const [expandedThreadIds, setExpandedThreadIds] = useState({});

  const toggleThreadReplies = (threadId) => {
    setExpandedThreadIds((prev) => ({
      ...prev,
      [threadId]: !prev[threadId],
    }));
  };

  useEffect(() => {
    dispatch(getEmailHistoryByLeadId(id));
  }, [id, dispatch]);

  const normalizeSubject = (subject = "") => {
    return subject
      .replace(/^(re:|fw:|fwd:)\s*/gi, "")
      .trim()
      .toLowerCase();
  };

  const formatEmailsByThread = (emails) => {
    const grouped = {};

    emails.forEach((email) => {
      const key = email.threadId
        ? email.threadId
        : normalizeSubject(email.subject) || "no-subject";

      if (!grouped[key]) {
        grouped[key] = [];
      }

      grouped[key].push(email);
    });

    const formattedArray = Object.entries(grouped).map(
      ([groupKey, groupEmails]) => {
        const sortedEmails = groupEmails.sort(
          (a, b) => new Date(a.createdDate) - new Date(b.createdDate),
        );

        return {
          groupKey,
          emails: sortedEmails,
        };
      },
    );

    return formattedArray;
  };

  const formattedEmails = formatEmailsByThread(emailConversationHistory || []);

  const toggleEmailExpansion = (emailId) => {
    setExpandedEmails((prev) => ({
      ...prev,
      [emailId]: !prev[emailId],
    }));
  };

  if (loading) {
    return <Loader />;
  }

  const hasHistory =
    emailConversationHistory && emailConversationHistory.length > 0;
  const isAnyEmailExpanded = Object.values(expandedEmails).some(Boolean);
  const isAnyThreadExpanded = Object.values(expandedThreadIds).some(Boolean);
  const isAnyExpanded = isAnyEmailExpanded || isAnyThreadExpanded;

  const handleCollapseAll = () => {
    setExpandedEmails({});
    setExpandedThreadIds({});
  };

  return (
    <div
      className={`email-history !tw-p-5 !tw-flex !tw-flex-col !tw-gap-5 ${
        hasHistory
          ? "has-history !tw-h-[calc(100vh-285px)] !tw-max-h-[calc(100vh-285px)] !tw-overflow-hidden"
          : "no-history"
      }`}
    >
      {/* Top Header & Compose Row */}
      <div className="tw-flex tw-justify-between tw-items-center tw-pb-2">
        <h3 className="tw-text-base tw-text-slate-800 tw-flex tw-items-center tw-gap-2.5 tw-m-0">
          <Email />
          <span className="tw-font-bold tw-tracking-tight">Email History</span>
        </h3>

        <div className="tw-flex tw-items-center tw-gap-2.5">
          {hasHistory && isAnyExpanded && (
            <Button
              onClick={handleCollapseAll}
              className="tw-flex tw-items-center tw-h-10 tw-gap-2 tw-text-slate-600 hover:tw-text-theme tw-border tw-border-solid tw-border-slate-200 hover:tw-border-theme/30 tw-bg-white hover:tw-bg-indigo-50/20 tw-font-semibold tw-rounded-xl tw-shadow-sm hover:tw-shadow tw-transition-all tw-px-3.5"
            >
              <i className="las la-compress-arrows-alt tw-text-base" />
              Collapse All
            </Button>
          )}

          {!!leadDetail?.emailId && (
            <EmailForm
              recipientEmail={leadDetail?.emailId}
              empId={leadDetail?.employeeId}
              payload={{
                ...leadDetail,
                emailHistoryList: emailConversationHistory
                  ?.slice(-3)
                  ?.map((d) => ({
                    subject: d?.subject,
                    bodyS3Url: d?.bodyS3Url,
                  })),
              }}
              callback={() => dispatch(getEmailHistoryByLeadId(leadDetail?.id))}
            >
              <Button className="tw-flex tw-items-center tw-h-10 tw-gap-2 tw-text-theme hover:tw-text-theme/90 tw-border tw-border-solid tw-border-theme/30 hover:tw-border-theme/60 tw-bg-indigo-50/50 hover:tw-bg-indigo-50 tw-font-semibold tw-rounded-xl tw-shadow-sm hover:tw-shadow tw-transition-all tw-px-4">
                <Email />
                Compose Email
              </Button>
            </EmailForm>
          )}
        </div>
      </div>

      {/* Conversation Threads Stack */}
      <div className="emails-list tw-flex tw-flex-col tw-gap-6 !tw-flex-1 !tw-min-h-0 !tw-overflow-y-auto tw-pr-1.5 tw-pb-6 custom-scrollbar">
        {formattedEmails?.map((item, i) => {
          const threadSubject = item.emails[0]?.subject || "No Subject";
          const messageCount = item.emails.length;
          const latestEmail = item.emails[messageCount - 1];
          return (
            <div
              key={i}
              className="tw-bg-white tw-rounded-2xl tw-border tw-border-solid tw-border-slate-200/60 tw-shadow-[0_4px_20px_rgba(0,0,0,0.02)] hover:tw-shadow-[0_8px_30px_rgba(93,68,255,0.03)] tw-transition-shadow tw-duration-300"
            >
              {/* Thread Container Header */}
              <div className="tw-bg-white tw-border-b tw-border-t-0 tw-border-l-0 tw-border-r-0 tw-border-solid tw-border-slate-100 tw-px-5 tw-py-4 tw-flex tw-justify-between tw-items-center tw-rounded-t-2xl">
                <div className="tw-flex tw-items-center tw-gap-2.5">
                  <span className="tw-text-sm tw-font-bold tw-text-slate-800 tw-tracking-tight">
                    {threadSubject}
                  </span>
                  {messageCount > 1 && (
                    <span className="tw-bg-indigo-50/80 tw-text-theme tw-border tw-border-solid tw-border-indigo-100/30 tw-text-[10px] tw-font-bold tw-px-2.5 tw-py-0.5 tw-rounded-full">
                      {messageCount} messages
                    </span>
                  )}
                </div>
                {/* Reply to Thread */}
                <EmailForm
                  recipientEmail={leadDetail?.emailId}
                  empId={leadDetail?.employeeId}
                  payload={{
                    ...leadDetail,
                    messageId: latestEmail?.messageId,
                    threadId: latestEmail?.threadId,
                    subject: threadSubject.toLowerCase().startsWith("re:")
                      ? threadSubject
                      : `Re: ${threadSubject}`,
                    emailHistoryList: emailConversationHistory
                      ?.slice(-3)
                      ?.map((d) => ({
                        subject: d?.subject,
                        bodyS3Url: d?.bodyS3Url,
                      })),
                  }}
                  callback={() =>
                    dispatch(getEmailHistoryByLeadId(leadDetail?.id))
                  }
                >
                  <Button className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-semibold tw-text-slate-600 hover:tw-text-theme tw-bg-slate-50 hover:tw-bg-indigo-50/50 tw-border tw-border-solid tw-border-slate-200 hover:tw-border-theme/30 tw-rounded-lg tw-px-3.5 tw-py-2 tw-transition-all tw-duration-200 tw-shadow-sm">
                    <i className="las la-reply tw-text-sm" /> Reply Thread
                  </Button>
                </EmailForm>
              </div>

              {/* Thread Messages */}
              <div className="tw-flex tw-flex-col tw-relative">
                {/* Connector line */}
                {messageCount > 1 &&
                  expandedThreadIds[item.emails[0]?.threadId || i] && (
                    <div className="tw-absolute tw-left-[36px] tw-top-[32px] tw-bottom-[32px] tw-w-[1.5px] tw-bg-slate-100 tw-pointer-events-none" />
                  )}

                {/* Always show the first (main) email */}
                {item.emails.slice(0, 1).map((data, index) => (
                  <EmailThreadMessage
                    key={data.id || index}
                    data={data}
                    leadDetail={leadDetail}
                    isExpanded={Boolean(expandedEmails[data.id])}
                    onToggle={() => toggleEmailExpansion(data.id)}
                    emailConversationHistory={emailConversationHistory}
                    isReply={false}
                    isLast={messageCount === 1}
                  />
                ))}

                {/* Show replies if expanded */}
                {messageCount > 1 &&
                  expandedThreadIds[item.emails[0]?.threadId || i] &&
                  item.emails
                    .slice(1)
                    .map((data, index) => (
                      <EmailThreadMessage
                        key={data.id || index + 1}
                        data={data}
                        leadDetail={leadDetail}
                        isExpanded={Boolean(expandedEmails[data.id])}
                        onToggle={() => toggleEmailExpansion(data.id)}
                        emailConversationHistory={emailConversationHistory}
                        isReply={true}
                        isLast={index + 1 === messageCount - 1}
                      />
                    ))}

                {/* Toggle Replies Action Bar */}
                {messageCount > 1 && (
                  <div className="tw-flex tw-justify-start tw-px-5 tw-py-3 tw-bg-slate-50/50 tw-border-t tw-border-b-0 tw-border-r-0 tw-border-l-0 tw-border-solid tw-border-slate-100/60">
                    <button
                      onClick={() =>
                        toggleThreadReplies(item.emails[0]?.threadId || i)
                      }
                      className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-font-semibold tw-text-slate-500 hover:tw-text-theme tw-bg-white hover:tw-bg-indigo-50/20 tw-border tw-border-solid tw-border-slate-200 hover:tw-border-theme/30 tw-rounded-lg tw-px-3 tw-py-1.5 tw-transition-all tw-duration-200 tw-shadow-sm hover:tw-shadow tw-cursor-pointer"
                    >
                      {expandedThreadIds[item.emails[0]?.threadId || i] ? (
                        <>
                          <i className="las la-angle-up tw-text-sm" />
                          Hide Replies
                        </>
                      ) : (
                        <>
                          <i className="las la-angle-down tw-text-sm" />
                          Show Replies ({messageCount - 1})
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {/* Empty State */}
        {!emailConversationHistory?.length && (
          <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-12">
            <img
              src={noEmailHistory}
              alt=""
              className="tw-w-[300px] tw-h-[250px] tw-opacity-60 tw-object-contain tw-mb-5"
            />
            <div className="tw-text-sm tw-text-slate-500 tw-text-center tw-max-w-[320px] tw-leading-relaxed">
              {!leadDetail?.emailId
                ? "No email is linked to your account. Please add an email to start conversations."
                : "No email history found for your account. Start sending emails to begin your conversations."}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailHistory;
